// angular
import { CommonModule, DOCUMENT, isPlatformBrowser, NgOptimizedImage } from '@angular/common';
import {
  AfterViewInit,
  afterNextRender,
  Component,
  ElementRef,
  effect,
  HostListener,
  inject,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import {
  NavigationStart,
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
// components
import { ButtonComponent } from '@app/core/components/button/button.component';
import { MegaMenuComponent } from '../mega-menu/mega-menu.component';
import { ProfileMenuComponent } from '../profile-menu/profile-menu.component';
import { HomeSearchSectionComponent } from '@app/+home/components/home-search-section/home-search-section.component';
import { LoginDialogComponent } from '@app/core/dialogs/login-dialog/login-dialog.component';
// services
import { LayoutService } from '@app/layout/shared/services/layout.service';
import { DialogService } from '@app/core/shared/services/dialog.service';
import { UserService } from '@app/shared/services/user.service';
import { CategoriesService } from '@app/+categories/shared/services/categories.service';
// types & envs
import { HeaderMenu } from '@app/shared/types/header-menu.type';
import { IUser } from '@app/shared/types/user.type';
import { ButtonsMenuTypes } from '@app/shared/types/languages.type';
import { CategoriesType } from '@app/+categories/shared/types/categories.type';
// utilities & constants
import { languagesData } from '@app/shared/constants/languages';
import { mainHeaderMenuData } from '@app/shared/constants/header-menu-data.const';
import {
  COMPANY_PHONE_LOCAL,
  COMPANY_PHONE_TEL,
} from '@app/shared/constants/company-contact.const';
// pipes & enums
import { HasAccessPipe } from '@app/shared/pipes/has-access.pipe';
import { Roles } from '@app/shared/enums/roles.enum';
import { MatomoTrackClickDirective } from 'ngx-matomo-client';

@Component({
  selector: 'pwj-header',
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    NgOptimizedImage,
    MegaMenuComponent,
    ProfileMenuComponent,
    RouterLink,
    RouterLinkActive,
    TranslateModule,
    NgxSkeletonLoaderModule,
    HomeSearchSectionComponent,
    MatomoTrackClickDirective,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit, AfterViewInit {
  public readonly companyPhoneLocal = COMPANY_PHONE_LOCAL;
  public readonly companyPhoneTel = COMPANY_PHONE_TEL;
  public mainMenu: HeaderMenu[] = mainHeaderMenuData;
  public languagesMenuItems: ButtonsMenuTypes[] = languagesData;
  public selectedLang!: string;
  public activeRoute: string = '';
  public user!: IUser;
  public hiddenMegaMenu: boolean = true;
  public hiddenAdvancedFilters: boolean = true;
  public hiddenProfileMenu: boolean = true;
  public showSearchBar!: boolean;
  public productCategories!: CategoriesType[];
  public mobileNavOpen = false;
  public isContentFixed = false;
  private mobileOpenSubKeys = new Set<string>();
  @ViewChild('topInfo') private topInfoRef?: ElementRef<HTMLElement>;

  // dependencies
  private readonly _router = inject(Router);
  private readonly _layoutService = inject(LayoutService);
  private readonly _translateService = inject(TranslateService);
  private readonly _userService = inject(UserService);
  private readonly _dialogService = inject(DialogService);
  private readonly _hasAccessPipe = inject(HasAccessPipe);
  private readonly _categoriesService = inject(CategoriesService);
  private readonly _document = inject(DOCUMENT);
  private readonly _platformId = inject(PLATFORM_ID);

  constructor() {
    effect(() => {
      this.hiddenMegaMenu = this._layoutService.hiddenMegaMenu();

      this.hiddenAdvancedFilters = this._layoutService.hiddenAdvancedFilters();

      this.hiddenProfileMenu = this._layoutService.hiddenProfileMenu();
    });

    // set selected language
    this.selectedLang = this._translateService.currentLang;
    this._translateService.onLangChange.subscribe(lang => {
      this.selectedLang = lang.lang;
    });

    // set user data
    this._userService.user$.subscribe((user: IUser) => (this.user = user));

    // track router url changes
    this._router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.activeRoute = event.url;
        this.updateSearchBarVisibility(event.url);
        // hidden profile menu on route change
        this._layoutService.hiddenProfileMenu.set(true);
        this.closeMobileNav();
      }
    });

    if (!this._hasAccessPipe.transform(Roles.ViewCustomers)) {
      this.mainMenu = this.mainMenu.filter(
        (menuItem: HeaderMenu) => !menuItem.name.includes('customers')
      );
    }

    effect(() => {
      this.productCategories = this._categoriesService.categoriesData();
    });

    afterNextRender(() => {
      if (this.productCategories?.length === 0) {
        // fetch categories data
        this._categoriesService.getCategories();
      }
    });
  }

  ngOnInit(): void {
    this.activeRoute = this._router.url;
    this.updateSearchBarVisibility(this._router.url);
  }

  ngAfterViewInit(): void {
    this.updateHeaderScrollState();
  }

  @HostListener('window:scroll')
  public onWindowScroll(): void {
    this.updateHeaderScrollState();
  }

  @HostListener('window:resize')
  public onWindowResize(): void {
    this.updateHeaderScrollState();
  }

  private updateHeaderScrollState(): void {
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }
    if (window.innerWidth < 768) {
      this.isContentFixed = false;
      return;
    }
    const topInfoHeight = this.topInfoRef?.nativeElement.offsetHeight ?? 0;
    this.isContentFixed = window.scrollY > topInfoHeight;
  }

  @HostListener('document:keydown.escape')
  protected onDocumentEscape(): void {
    if (this.mobileNavOpen) {
      this.closeMobileNav();
    }
  }

  public toggleMobileNav(): void {
    this.mobileNavOpen = !this.mobileNavOpen;
    this._layoutService.mobileNavOpen.set(this.mobileNavOpen);
    if (this.mobileNavOpen) {
      this.resetMobileSubmenus();
      this._layoutService.hiddenMegaMenu.set(true);
      this._layoutService.hiddenAdvancedFilters.set(true);
      this._layoutService.hiddenProfileMenu.set(true);
      this._layoutService.windowIsOpen.set(false);
      this.setBodyScrollLocked(true);
    } else {
      this.setBodyScrollLocked(false);
    }
  }

  public closeMobileNav(): void {
    if (!this.mobileNavOpen) {
      return;
    }
    this.mobileNavOpen = false;
    this._layoutService.mobileNavOpen.set(false);
    this.setBodyScrollLocked(false);
  }

  /** Highlight for top-level items without relying on RouterLink (parent uses programmatic nav). */
  public isMenuItemActive(item: HeaderMenu): boolean {
    const url = this._router.url.split('?')[0].split('#')[0];
    const path = url.length > 1 && url.endsWith('/') ? url.slice(0, -1) : url;
    if (item.exact) {
      return path === item.link || path === '';
    }
    if (item.children?.length) {
      if (path === item.link || path.startsWith(`${item.link}/`)) {
        return true;
      }
      return item.children.some(
        child => path === child.link || path.startsWith(`${child.link}/`)
      );
    }
    return path === item.link || path.startsWith(`${item.link}/`);
  }

  /**
   * Programmatic navigation so /solutions (and other parents) always navigates; RouterLink on
   * the same control as RouterLinkActive was unreliable here. Modifier-clicks keep browser defaults.
   */
  public onParentMenuNavigate(item: HeaderMenu, event: MouseEvent): void {
    this.closeMobileNav();
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    void this._router.navigateByUrl(item.link);
  }

  public onMobileDropdownToggle(event: MouseEvent, item: HeaderMenu): void {
    if (!item.children?.length || !this.isMobileViewport()) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const key = this.getMenuItemKey(item);
    const nextKeys = new Set(this.mobileOpenSubKeys);
    if (nextKeys.has(key)) {
      nextKeys.delete(key);
    } else {
      nextKeys.add(key);
    }
    this.mobileOpenSubKeys = nextKeys;
  }

  public isMobileSubmenuOpen(item: HeaderMenu): boolean {
    if (!item.children?.length) {
      return false;
    }
    return this.mobileOpenSubKeys.has(this.getMenuItemKey(item));
  }

  private resetMobileSubmenus(): void {
    this.mobileOpenSubKeys = new Set(
      this.mainMenu
        .filter(menuItem => menuItem.children?.length)
        .map(menuItem => this.getMenuItemKey(menuItem))
    );
  }

  private getMenuItemKey(item: HeaderMenu): string {
    return item.link ?? item.name;
  }

  private isMobileViewport(): boolean {
    return (
      isPlatformBrowser(this._platformId) &&
      window.matchMedia('(max-width: 47.99em)').matches
    );
  }

  private setBodyScrollLocked(lock: boolean): void {
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }
    this._document.body.classList.toggle('no-scroll', lock);
    this._document.documentElement.classList.toggle('no-scroll', lock);
  }

  public toggleMegaMenu(menuItem?: string) {
    if (menuItem === '/categories') {
      this._layoutService.hiddenMegaMenu.set(false);
      this._layoutService.windowIsOpen.set(true);
    } else {
      this._layoutService.hiddenMegaMenu.set(true);
      this.hiddenAdvancedFilters &&
        this.hiddenProfileMenu &&
        this._layoutService.windowIsOpen.set(false);
    }
  }

  public toggleAdvancedFilters() {
    this._layoutService.hiddenAdvancedFilters.set(!this.hiddenAdvancedFilters);
    this._layoutService.windowIsOpen.set(!this.hiddenAdvancedFilters);
  }

  public toggleProfileMenu() {
    this.closeMobileNav();
    if (this.user.username) {
      this._layoutService.hiddenProfileMenu.set(false);
      this._layoutService.windowIsOpen.set(true);
    } else {
      const dialogRef = this._dialogService.open(LoginDialogComponent, {
        width: 'none',
        height: 'none',
      });

      dialogRef.afterClosed().subscribe(res => {});
    }
  }

  public closeProfileMenu(e: MouseEvent, headerContent: Element) {
    if (this.hiddenProfileMenu === false && e.target === headerContent) {
      this._layoutService.windowIsOpen.set(false);
      this._layoutService.hiddenAdvancedFilters.set(true);
      this._layoutService.hiddenProfileMenu.set(true);
    }
  }

  private updateSearchBarVisibility(url: string): void {
    const cleanUrl = url.split('?')[0].split('#')[0];
    this.showSearchBar =
      cleanUrl === '/solutions' ||
      cleanUrl.startsWith('/solutions/') ||
      cleanUrl === '/products' ||
      cleanUrl.startsWith('/products/') ||
      cleanUrl === '/auctions' ||
      cleanUrl.startsWith('/auctions/') ||
      cleanUrl === '/tenders' ||
      cleanUrl.startsWith('/tenders/') ||
      cleanUrl === '/search-results' ||
      cleanUrl.startsWith('/search-results/');
  }

  public setLang(lang: string) {
    this.selectedLang = lang;
    // change language to lowercase
    this._translateService.use(lang.toLowerCase());
    // set profile lang
    if (this.user.username) {
      this._userService.updateProfileLanguage(lang.toLowerCase());
    }
  }
}
